import { Component, OnDestroy } from '@angular/core';
import { RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { AuthModalComponent } from './shared/modals/auth-modal/auth-modal.component';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from './shared/services/auth.service';
import { LoaderComponent } from './components/loader/loader.component';
import { WaseelWidgetLoaderComponent } from './components/waseel-widget-loader/waseel-widget-loader.component';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, RouterOutlet, AuthModalComponent, LoaderComponent, WaseelWidgetLoaderComponent],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnDestroy {
  title = 'insurance-aggregator-ui';
  currentDir = 'ltr';
  isInitialized = false;
  isAuthenticated = false;
  isLoading = true;

  private subscriptions = new Subscription();
  private hasProcessedInitialAuth = false;
  private isNavigating = false;

  constructor(
    private translateService: TranslateService,
    public authService: AuthService,
    private router: Router
  ) {
    this.translateService.setDefaultLang('en');
    this.translateService.onLangChange.subscribe((event) => {
      this.currentDir = event.lang === 'ar' ? 'rtl' : 'ltr';
    });

    // Log all navigation events to debug redirect issues
    this.router.events.subscribe((event: any) => {
      if (event.constructor.name === 'NavigationStart') {
        console.log('📍 Router NavigationStart:', event.url);
      } else if (event.constructor.name === 'NavigationEnd') {
        console.log('✅ Router NavigationEnd:', event.urlAfterRedirects);
      } else if (event.constructor.name === 'NavigationError') {
        console.error('❌ Router NavigationError:', event.error);
      }
    });
  }

  async ngOnInit() {
    try {
      this.isLoading = true;

      this.authService.setupSilentCheckSsoCallback();

      // Subscribe to authentication state
      const authSub = this.authService.isAuthenticated$.subscribe((auth) => {
        this.isAuthenticated = auth;
        console.log('🔐 Authentication state changed:', auth, 'hasProcessedInitialAuth:', this.hasProcessedInitialAuth);

        // Handle authentication state changes - ONLY on first time auth
        if (auth && !this.hasProcessedInitialAuth) {
          this.hasProcessedInitialAuth = true;
          console.log('🔐 First time authentication detected - calling handlePostLoginRedirect');
          this.handlePostLoginRedirect();
        }
        // Don't add else-if logic here - it causes redirect loops
      });

      // Subscribe to initialization state
      const initSub = this.authService.isInitialized$.subscribe((init) => {
        this.isInitialized = init;
        this.isLoading = !init;

        if (init) {
          console.log('✅ Auth service initialized');
          // Don't set hasProcessedInitialAuth here - let it be controlled by auth state
        }
      });

      this.subscriptions.add(authSub);
      this.subscriptions.add(initSub);
    } catch (error) {
      console.error('Component initialization error:', error);
      this.isInitialized = true;
      this.isLoading = false;
    }
  }

  /**
   * Detect if this is a fresh login from Keycloak (not a page reload)
   */
  private isFreshLogin(): boolean {
    // Method 1: Check for Keycloak callback parameters in URL
    const urlParams = new URLSearchParams(window.location.search);
    const hashParamsString = window.location.hash.startsWith('#')
      ? window.location.hash.substring(1)
      : window.location.hash;
    const hashParams = new URLSearchParams(hashParamsString);

    const hasKeycloakParams =
      urlParams.has('state') ||
      urlParams.has('code') ||
      urlParams.has('session_state') ||
      hashParams.has('state') ||
      hashParams.has('code') ||
      hashParams.has('session_state');

    console.log('Fresh login check - URL params:', {
      state: urlParams.has('state'),
      code: urlParams.has('code'),
      session_state: urlParams.has('session_state'),
      hash_state: hashParams.has('state'),
      hash_code: hashParams.has('code'),
      hash_session_state: hashParams.has('session_state'),
      currentUrl: window.location.href,
    });

    if (hasKeycloakParams) {
      console.log('Detected Keycloak login callback parameters');
      return true;
    }

    // Method 2: Check if there's a stored login redirect path
    const loginRedirectPath = sessionStorage.getItem('loginRedirectPath');
    if (loginRedirectPath) {
      console.log('Detected stored login redirect path:', loginRedirectPath);
      return true;
    }

    // Method 3: Check if router has not navigated yet (first navigation)
    // This is less reliable but can be used as additional check
    if (!this.router.navigated) {
      // Check if we have tokens but no previous session marker
      const hasTokens = localStorage.getItem('keycloakToken') !== null;
      const hadPreviousSession =
        sessionStorage.getItem('had_session') === 'true';

      if (hasTokens && !hadPreviousSession) {
        console.log('Detected first navigation with new tokens');
        return true;
      }
    }

    return false;
  }

  /**
   * Handle redirect after fresh login
   */
  private handlePostLoginRedirect(): void {
    console.log('🔄 handlePostLoginRedirect called');
    
    // Prevent multiple simultaneous navigations
    if (this.isNavigating) {
      console.log('Navigation already in progress, skipping duplicate redirect');
      return;
    }
    
    if (sessionStorage.getItem('processing_payment_callback') === 'true') {
      console.log('Payment callback in progress, skipping redirect');
      return;
    }
    
    const urlParams = new URLSearchParams(window.location.search);
    const hashParamsString = window.location.hash.startsWith('#')
      ? window.location.hash.substring(1)
      : window.location.hash;
    const hashParams = new URLSearchParams(hashParamsString);

    const hasKeycloakParams =
      urlParams.has('state') ||
      urlParams.has('code') ||
      urlParams.has('session_state') ||
      hashParams.has('state') ||
      hashParams.has('code') ||
      hashParams.has('session_state');

    console.log('🔍 URL Analysis:', {
      currentUrl: window.location.href,
      hasKeycloakParams,
      keycloakParams: {
        state: urlParams.get('state'),
        code: urlParams.get('code'),
        session_state: urlParams.get('session_state'),
        hash_state: hashParams.get('state'),
        hash_code: hashParams.get('code'),
        hash_session_state: hashParams.get('session_state'),
      }
    });

    if (hasKeycloakParams) {
      // Remove Keycloak parameters from URL but preserve the path
      console.log('🧹 Cleaning Keycloak parameters from URL');
      const cleanPath = window.location.pathname;
      window.history.replaceState({}, document.title, cleanPath);
    }

    // Get redirect destination
    const loginRedirectPath = sessionStorage.getItem('loginRedirectPath');
    const returnUrl = urlParams.get('returnUrl');
    const currentPath = this.router.url.split('?')[0]; // Get path without query params

    console.log('📍 Redirect Analysis:', {
      currentPath,
      loginRedirectPath,
      returnUrl,
      isAuthenticated: this.isAuthenticated
    });

    let redirectPath = '/dashboard/home'; // Default to specific dashboard page

    // Determine where to redirect
    if (
      loginRedirectPath &&
      loginRedirectPath !== '/' &&
      loginRedirectPath !== '' &&
      loginRedirectPath.startsWith('/dashboard')
    ) {
      redirectPath = loginRedirectPath;
      console.log('✅ Using stored redirect path:', redirectPath);
    } else if (returnUrl && returnUrl.startsWith('/dashboard')) {
      redirectPath = returnUrl;
      console.log('✅ Using returnUrl:', redirectPath);
    } else if (currentPath === '/' || currentPath === '' || currentPath === '/login') {
      redirectPath = '/dashboard/home';
      console.log('✅ Redirecting to dashboard from landing/login page');
    } else if (currentPath.startsWith('/dashboard')) {
      // Already on dashboard, don't redirect
      console.log('✅ Already on dashboard, staying at:', currentPath);
      sessionStorage.setItem('had_session', 'true');
      sessionStorage.removeItem('loginRedirectPath');
      return;
    } else {
      // On another page, redirect to dashboard anyway for authenticated users
      redirectPath = '/dashboard/home';
      console.log('✅ Authenticated user on other page, redirecting to dashboard');
    }

    // Clear stored redirect path
    sessionStorage.removeItem('loginRedirectPath');

    // Mark that we have an active session
    sessionStorage.setItem('had_session', 'true');

    // Perform redirect
    console.log('🚀 Performing redirect from', currentPath, 'to', redirectPath);
    
    if (currentPath !== redirectPath) {
      // Use setTimeout to ensure the redirect happens after the current execution cycle
      this.isNavigating = true;
      console.log('⏳ Setting isNavigating to true, scheduling navigation...');
      setTimeout(() => {
        console.log('⏳ timeout callback: navigating to', redirectPath);
        this.router.navigate([redirectPath], { replaceUrl: true }).then((success) => {
          console.log('✅ Navigation promise resolved, success:', success);
          this.isNavigating = false;
        }).catch((err) => {
          console.error('❌ Navigation promise rejected:', err);
          this.isNavigating = false;
        });
      }, 100);
    } else {
      console.log('ℹ️ Already at target path, no redirect needed');
    }
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  isSidenavVisible: boolean = false;

  toggleSidenav() {
    this.isSidenavVisible = !this.isSidenavVisible;
  }
}
